/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import Vue from 'vue';
import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import createLogger from 'vuex/dist/logger';
import { find, uniqueId } from 'lodash';

import { RootState } from '@/state/types';
import { keysToCamel, keysToSnake } from '@/utils';
import { generateSaveQuery } from './helpers';
import { Block, RelatedFilter } from './types';
import { postRequest, getRequest, deleteRequest } from '../../utils/client/client';

const debug = process.env.NODE_ENV !== 'production';

interface Configuration {
  id: number;
  title: string;
}

interface State {
  configuration: Block[];
  configurationList: Configuration[];
}

const state: State = {
  configuration: [],
  configurationList: [],
};

const mutations: MutationTree<State> = {
  UPDATE_CONFIGURATION_LIST(state: State, newConfigs: Configuration[]) {
    state.configurationList = newConfigs;
  },
  UPDATE_ENTIRE_CONFIGURATION(state: State, newConfig: Block[]) {
    state.configuration = newConfig;
  },
  ADD_BLOCK(state: State, newBlock: Block) {
    state.configuration.push(newBlock);
  },
  UPDATE_BLOCK(state: State, { index, newBlock }: { index: number; newBlock: Block }) {
    Vue.set(state.configuration, index, newBlock);
  },
  DELETE_BLOCK(state: State, index: number) {
    state.configuration.splice(index, 1);
    if (index === 0 && state.configuration.length > 0 && state.configuration[0].isRelevantToPrevious) {
      state.configuration[0].isRelevantToPrevious = false;
    }
  },
};

const actions: ActionTree<State, RootState> = {
  clearConfig({ commit }: ActionContext) {
    commit('UPDATE_ENTIRE_CONFIGURATION', []);
  },
  async addEmptyBlock({ commit, dispatch, rootGetters }: ActionContext) {
    const newBlock = await dispatch('loadEmptyBlock', rootGetters.exportableObjects[0].modelSingular);
    commit('ADD_BLOCK', newBlock);
  },
  updateBlock({ commit }: ActionContext, { index, newBlock }: { index: number; newBlock: Block }) {
    commit('UPDATE_BLOCK', { index, newBlock });
  },
  deleteBlock({ commit }: ActionContext, index: number) {
    commit('DELETE_BLOCK', index);
  },

  // Server calls
  loadConfigList({ commit, rootGetters }: ActionContext) {
    return new Promise((resolve, reject) => {
      const url = rootGetters.exportEndpoints.config.replace('__CONFIG_ID__', '');
      getRequest({ url })
        .then(responseData => {
          commit('UPDATE_CONFIGURATION_LIST', keysToCamel(responseData));
          resolve();
        })
        .catch(reject);
    });
  },
  loadConfig({ commit, rootGetters }: ActionContext, id: number) {
    return new Promise((resolve, reject) => {
      const url = rootGetters.exportEndpoints.config.replace('__CONFIG_ID__', String(id));
      getRequest({ url })
        .then(responseData => {
          const newConfig = keysToCamel(responseData) as Block[];
          newConfig.forEach(block => {
            block.id = uniqueId();
          });
          commit('UPDATE_ENTIRE_CONFIGURATION', newConfig);
          resolve();
        })
        .catch(reject);
    });
  },
  saveConfig({ getters, rootGetters }: ActionContext, title: string) {
    return new Promise((resolve, reject) => {
      const query = generateSaveQuery(getters.configuration);
      postRequest({
        url: rootGetters.exportEndpoints.saveConfig,
        data: keysToSnake({ query, title }),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': rootGetters.csrfToken,
        },
      })
        .then(responseData => {
          resolve(responseData);
        })
        .catch(error => {
          reject(error.response.data);
        });
    });
  },
  deleteConfig({ rootGetters }: ActionContext, id: number) {
    return new Promise((resolve, reject) => {
      const url = rootGetters.exportEndpoints.config.replace('__CONFIG_ID__', String(id));
      deleteRequest({ url, headers: { 'X-CSRF-Token': rootGetters.csrfToken } })
        .then(resolve)
        .catch(reject);
    });
  },
  loadEmptyBlock({ rootGetters }: ActionContext, objectType: string) {
    return new Promise((resolve, reject) => {
      const url = rootGetters.exportEndpoints.block.replace('__OBJECT_TYPE__', objectType);
      getRequest({ url })
        .then(responseData => {
          const newBlock = keysToCamel(responseData) as Block;
          newBlock.id = uniqueId();
          newBlock.relatedFilters.forEach(filter => {
            filter.id = uniqueId();
          });
          resolve(newBlock);
        })
        .catch(reject);
    });
  },
  loadEmptyFilter({ rootGetters }: ActionContext, relevantType: string) {
    return new Promise((resolve, reject) => {
      const url = rootGetters.exportEndpoints.filter.replace('__RELEVANT_TYPE__', relevantType);
      getRequest({ url })
        .then(responseData => {
          const newFilter = keysToCamel(responseData) as RelatedFilter;
          if (newFilter.objects.length > 0) {
            newFilter.objectId = newFilter.objects[0].id;
          }
          newFilter.id = uniqueId();
          resolve(newFilter);
        })
        .catch(reject);
    });
  },
  generateReport({ getters, rootGetters }: ActionContext, stripHtmlInExport: boolean) {
    return new Promise((resolve, reject) => {
      const url = rootGetters.exportEndpoints.generateReport;
      const filterData = generateSaveQuery(getters.configuration);
      const data = {
        filter: filterData,
        stripHtmlInExport: stripHtmlInExport,
      };
      const headers = {
        'Content-Type': 'application/json',
        'X-export-view': 'blocks',
        'X-requested-by': 'gGRC',
        'X-CSRFToken': rootGetters.csrfToken,
      };

      postRequest({ url, data, headers })
        .then(responseData => {
          resolve(responseData);
        })
        .catch(reject);
    });
  },
};

const getters: GetterTree<State, RootState> = {
  selectedConfiguration(_, getters: Getters, rootState: RootState) {
    if (rootState.route.params.configId) {
      const id = Number(rootState.route.params.configId);
      return find(getters.configurationList, { id });
    }
    return null;
  },
  configuration(state: State) {
    return state.configuration;
  },
  configurationList(state: State) {
    return state.configurationList;
  },
};

export default (): Module<State, RootState> => ({
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  strict: debug,
  plugins: debug ? [createLogger()] : [],
});
